/**
 * Canvas component implemented with Material UI
 * 
 * This component provides a customizable canvas element with styling options
 * for different use cases like legends or absolute positioning.
 */
import React, { forwardRef } from 'react';
import { styled } from '@mui/material/styles';
import { CanvasProps } from 'types/components/atoms';

// Interface for styled canvas props
interface StyledCanvasProps {
  legend?: boolean;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

/**
 * Canvas component with proper ref forwarding
 * 
 * @param props - Component props
 * @param ref - Forwarded ref to the canvas element
 * @returns Canvas component with customized styling
 */
const Canvas = forwardRef<HTMLCanvasElement, CanvasProps>((props, ref) => {
  return <StyledCanvas ref={ref} {...props} />;
});

// Add display name for debugging purposes
Canvas.displayName = 'Canvas';

// Styled components using MUI styled API
const StyledCanvas = styled('canvas')<StyledCanvasProps>(
  ({ theme, legend, top, left = 0, width, height }) => ({
    position: legend ? 'relative' : 'absolute',
    top: legend ? 'auto' : `${top}px`,
    left: legend ? 'auto' : `${left}px`,
    width: legend ? '-webkit-fill-available' : `${width}px`,
    height: legend ? 'auto' : `${height}px`, // Let legend height be determined by the canvas itself
    border: legend ? '1px solid var(--app-custom-gray)' : 'none',
    borderRadius: legend ? '4px' : '0px',
    zIndex: legend ? 'auto' : 0, // Heatmap canvases in background
    // Canvas clipping is handled through dimension constraints, not CSS clipPath
    overflow: 'hidden',
  })
);

export type { CanvasProps };
export default Canvas;
